import { Icon, Scrollbar } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Button, Checkbox, Stack, Typography } from '@mui/material';
import { memo, MouseEvent, useRef } from 'react';
import { TemplateOptions } from '../template-options';
import * as Styled from './styles';
import * as SX from './sx';
import type { TemplateEntryProps as Props } from './types';

const ACCORDION_SLOT_PROPS = { transition: { timeout: 0 } };

export const TemplateEntry = memo(
  ({
    className,
    entry,
    isSingle,
    expanded,
    checkedValues,
    onToggle,
    onSelectAll,
    onDeselectAll,
    onActiveEntry,
    onRemove,
    onToggleExpanded,
    onToggleEntryChecked,
    selectedOptionsCountInEntry,
  }: Props) => {
    const hasSelectedOption = entry.fields?.some((i) => i.selected);
    const ref = useRef<HTMLDivElement>(null);

    const handleExpand = (
      e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>
    ) => {
      if (ref && ref.current && (e.target as HTMLElement).getAttribute('rel')) {
        return;
      }

      if (entry.notAdded) return;
      onToggleExpanded();
    };

    const handleToggleEntryChecked = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (entry.notAdded) return;

      onToggleEntryChecked();
    };

    const handleDeselectAll = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      if (hasSelectedOption) onDeselectAll();
    };

    const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onRemove?.();
    };

    const handleActiveEntry = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onActiveEntry?.();
      onToggleExpanded();
    };

    return (
      <Styled.Wrapper
        className={classnames('TemplateCategory', {
          className,
          'not-added': entry.notAdded,
          'is-single': isSingle,
        })}
        sx={SX.root}
        expanded={expanded}
        slotProps={ACCORDION_SLOT_PROPS}
      >
        {!isSingle && (
          <Styled.Summary onClick={(e) => handleExpand(e)} ref={ref}>
            <Scrollbar>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={SX.summaryContent}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  <Box
                    display={'inline-flex'}
                    onClick={handleToggleEntryChecked}
                  >
                    <Checkbox
                      rel="toggle-entry"
                      size="small"
                      sx={{
                        cursor:
                          entry.notAdded && entry.isBottom
                            ? 'not-allowed'
                            : 'pointer',
                      }}
                      checked={entry.checked}
                      disabled={entry.notAdded && entry.isBottom}
                    />
                  </Box>

                  <Typography variant="body3" fontWeight={500} color="grey.700">
                    {entry.notAdded ? (
                      <Typography
                        variant="body3"
                        fontWeight={500}
                        color="grey.700"
                      >
                        {entry.name}
                      </Typography>
                    ) : (
                      `${entry.name} (${selectedOptionsCountInEntry}) `
                    )}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                >
                  {entry.notAdded ? (
                    <Button
                      variant="outlined"
                      size="xsmall"
                      startIcon={<Icon name="plus" />}
                      onClick={handleActiveEntry}
                    >
                      Add
                    </Button>
                  ) : (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      {!expanded ? (
                        <Button
                          variant="text"
                          color="default"
                          size="xsmall"
                          onClick={handleRemove}
                        >
                          Remove
                        </Button>
                      ) : (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <Box
                            display={'inline-flex'}
                            onClick={handleDeselectAll}
                          >
                            <Button
                              variant="text"
                              color="default"
                              size="xsmall"
                              rel="deselect-all"
                              disabled={!hasSelectedOption}
                            >
                              Deselect All
                            </Button>
                          </Box>

                          <Button
                            variant="text"
                            color="default"
                            size="xsmall"
                            rel="select-all"
                            onClick={onSelectAll}
                          >
                            Select All
                          </Button>
                        </Stack>
                      )}
                      <Icon
                        name={expanded ? 'chevron-up' : 'chevron-down'}
                        size={20}
                        color="grey.500"
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Scrollbar>
          </Styled.Summary>
        )}
        {!entry.notAdded && (
          <Styled.Detail>
            {isSingle && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap={0.5}
              >
                <Button
                  variant="text"
                  color="default"
                  size="xsmall"
                  rel="deselect-all"
                  onClick={onDeselectAll}
                >
                  Deselect All
                </Button>

                <Button
                  variant="text"
                  color="default"
                  size="xsmall"
                  rel="select-all"
                  onClick={onSelectAll}
                >
                  Select All
                </Button>
              </Stack>
            )}

            <TemplateOptions
              options={entry.fields}
              checkedValues={checkedValues}
              onToggle={onToggle}
            />
          </Styled.Detail>
        )}
      </Styled.Wrapper>
    );
  }
);

TemplateEntry.displayName = 'TemplateEntry';
